import { useState, useEffect } from "react"
import { useGetProductsQuery, useGetProductByIdQuery, useModerateProductMutation, useUpdateProductStatusMutation } from "../features/admin/adminApi"
import { Check, X, Package, Search, Filter, Eye, Trash2 } from "lucide-react"

export default function ProductsPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set())
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null)

  const { data, isLoading, error } = useGetProductsQuery({ page, search: debouncedSearch, status: statusFilter })
  const { data: productDetails, isLoading: isLoadingDetails } = useGetProductByIdQuery(selectedProductId!, {
    skip: !selectedProductId,
  })
  const [moderate] = useModerateProductMutation()
  const [updateStatus] = useUpdateProductStatusMutation()

  // Clear selected products when page or filters change
  useEffect(() => {
    setSelectedProducts(new Set())
  }, [page, debouncedSearch, statusFilter])

  const handleSearch = (value: string) => {
    setSearch(value)
    const timer = setTimeout(() => setDebouncedSearch(value), 500)
    return () => clearTimeout(timer)
  }

  const handleModerate = async (uid: string, action: "approve" | "reject") => {
    if (!confirm(`Are you sure you want to ${action} this product?`)) return
    try {
      await moderate({ uid, action }).unwrap()
      alert(`Product ${action}d successfully`)
    } catch (err: any) {
      alert(err?.data?.message || `Failed to ${action} product`)
    }
  }

  const handleStatusChange = async (uid: string, status: string) => {
    try {
      await updateStatus({ uid, status }).unwrap()
    } catch (err: any) {
      alert(err?.data?.message || "Failed to update status")
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active": return "bg-green-100 text-green-800"
      case "inactive": return "bg-gray-100 text-gray-800"
      case "draft": return "bg-yellow-100 text-yellow-800"
      case "out_of_stock": return "bg-red-100 text-red-800"
      default: return "bg-gray-100 text-gray-800"
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked && data?.products) {
      setSelectedProducts(new Set(data.products.map(p => p.id)))
    } else {
      setSelectedProducts(new Set())
    }
  }

  const handleSelectProduct = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedProducts)
    if (checked) {
      newSelected.add(id)
    } else {
      newSelected.delete(id)
    }
    setSelectedProducts(newSelected)
  }

  const handleBulkStatusChange = async (status: string) => {
    if (selectedProducts.size === 0) return
    if (!confirm(`Change ${selectedProducts.size} product(s) to ${status}?`)) return

    try {
      await Promise.all(
        Array.from(selectedProducts).map(uid => updateStatus({ uid, status }).unwrap())
      )
      setSelectedProducts(new Set())
      alert("Products updated successfully")
    } catch (err: any) {
      alert(err?.data?.message || "Failed to update products")
    }
  }

  const handleBulkDelete = async () => {
    if (selectedProducts.size === 0) return
    if (!confirm(`Delete ${selectedProducts.size} product(s)? This will set them to inactive.`)) return

    await handleBulkStatusChange("inactive")
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Package size={28} />
          Products Management
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage, moderate, and monitor all products on the platform
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-4 items-center flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter size={18} className="text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="draft">Draft</option>
            <option value="out_of_stock">Out of Stock</option>
          </select>
        </div>
      </div>

      {/* Stats & Bulk Actions */}
      {data && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-blue-900">
              Showing <strong>{data.products.length}</strong> of <strong>{data.total}</strong> products
              {debouncedSearch && ` matching "${debouncedSearch}"`}
              {statusFilter && ` with status "${statusFilter}"`}
              {selectedProducts.size > 0 && (
                <span className="ml-2">
                  — <strong>{selectedProducts.size}</strong> selected
                </span>
              )}
            </p>
            {selectedProducts.size > 0 && (
              <div className="flex gap-2">
                <button
                  onClick={() => handleBulkStatusChange("active")}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition"
                >
                  Set Active
                </button>
                <button
                  onClick={() => handleBulkStatusChange("inactive")}
                  className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition"
                >
                  Set Inactive
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition flex items-center gap-1"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-gray-500">Loading products...</div>
        ) : error ? (
          <div className="p-12 text-center text-red-600">Failed to load products</div>
        ) : !data?.products.length ? (
          <div className="p-12 text-center text-gray-500">
            <Package size={48} className="mx-auto mb-3 text-gray-300" />
            <p>No products found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 w-12">
                    <input
                      type="checkbox"
                      checked={selectedProducts.size > 0 && selectedProducts.size === data.products.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Image</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Business</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Price</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Stock</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedProducts.has(product.id)}
                        onChange={(e) => handleSelectProduct(product.id, e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded border border-gray-200"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                          <Package size={20} className="text-gray-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{product.name}</p>
                        {product.description && (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{product.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{product.businessName || product.businessUid}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{product.categoryName || "-"}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">₦{product.price.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${product.stock > 10 ? "text-green-600" : product.stock > 0 ? "text-yellow-600" : "text-red-600"}`}>
                        {product.stock}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={product.status}
                        onChange={(e) => handleStatusChange(product.id, e.target.value)}
                        className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(product.status)}`}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="draft">Draft</option>
                        <option value="out_of_stock">Out of Stock</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {new Date(product.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setSelectedProductId(product.id)}
                          title="View Details"
                          className="p-1.5 rounded-lg transition-colors text-blue-600 hover:bg-blue-50"
                        >
                          <Eye size={16} />
                        </button>
                        {product.status === "draft" && (
                          <>
                            <button
                              onClick={() => handleModerate(product.id, "approve")}
                              className="p-1.5 hover:bg-green-50 rounded text-green-600"
                              title="Approve"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={() => handleModerate(product.id, "reject")}
                              className="p-1.5 hover:bg-red-50 rounded text-red-600"
                              title="Reject"
                            >
                              <X size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Page {page} of {data.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
              disabled={page === data.totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Product Details Modal */}
      {selectedProductId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Product Details</h2>
              <button
                onClick={() => setSelectedProductId(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {isLoadingDetails ? (
              <div className="p-8 text-center text-gray-500">Loading details...</div>
            ) : productDetails ? (
              <div className="p-6 space-y-6">
                {/* Product Images Gallery */}
                {productDetails.media && productDetails.media.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Product Images</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {productDetails.media.map((img) => (
                        <div key={img.id} className="relative">
                          <img
                            src={img.url}
                            alt={productDetails.name}
                            className="w-full h-32 object-cover rounded-lg border border-gray-200"
                          />
                          {img.isPrimary && (
                            <span className="absolute top-2 left-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                              Primary
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Product Info */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{productDetails.name}</h3>
                  <div className="flex gap-2 mb-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(productDetails.status)}`}>
                      {productDetails.status}
                    </span>
                    {productDetails.category && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {productDetails.category.name}
                      </span>
                    )}
                    {productDetails.subCategory && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        {productDetails.subCategory.name}
                      </span>
                    )}
                  </div>
                  {productDetails.description && (
                    <p className="text-gray-600">{productDetails.description}</p>
                  )}
                </div>

                {/* Pricing & Stock */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Pricing & Inventory</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Price:</span>
                      <p className="font-semibold text-lg text-gray-900">₦{productDetails.price.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Stock:</span>
                      <p className={`font-semibold text-lg ${productDetails.stock > 10 ? "text-green-600" : productDetails.stock > 0 ? "text-yellow-600" : "text-red-600"}`}>
                        {productDetails.stock} units
                      </p>
                    </div>
                  </div>
                </div>

                {/* Product Variants */}
                {productDetails.variants && productDetails.variants.length > 0 && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Product Variants</h4>
                    <div className="space-y-2">
                      {productDetails.variants.map((variant) => (
                        <div key={variant.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{variant.name}: {variant.value}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">₦{variant.price.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">Stock: {variant.stock}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Business Information */}
                {productDetails.business && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Business</h4>
                    <div className="text-sm space-y-2">
                      <div>
                        <span className="font-medium text-gray-700">Name:</span>
                        <span className="ml-2 text-gray-600">{productDetails.business.name}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Business ID:</span>
                        <span className="ml-2 text-gray-500 text-xs font-mono">{productDetails.business.id}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Owner Information */}
                {productDetails.owner && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Business Owner</h4>
                    <div className="text-sm space-y-2">
                      <div>
                        <span className="font-medium text-gray-700">Name:</span>
                        <span className="ml-2 text-gray-600">{productDetails.owner.name}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Email:</span>
                        <a href={`mailto:${productDetails.owner.email}`} className="ml-2 text-blue-600 hover:underline">
                          {productDetails.owner.email}
                        </a>
                      </div>
                      {productDetails.owner.phone && (
                        <div>
                          <span className="font-medium text-gray-700">Phone:</span>
                          <a href={`tel:${productDetails.owner.phone}`} className="ml-2 text-blue-600 hover:underline">
                            {productDetails.owner.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="border-t pt-4 text-xs text-gray-500 space-y-1">
                  <p>Created: {new Date(productDetails.createdAt).toLocaleString()}</p>
                  <p>Last Updated: {new Date(productDetails.updatedAt).toLocaleString()}</p>
                  <p>Product ID: <span className="font-mono">{productDetails.id}</span></p>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-red-500">Failed to load product details</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
